/**
 * Capability key per export card on /admin?tab=data-export, plus the roles that
 * already imply each one.
 *
 * These MIRROR the server guards exactly — the server is the control, this only
 * decides what to render. Keep them in step when adding an export:
 *   7 usage-analytics exports : RequirePermission("export:<name>", EXPORT_ROLES)
 *     (bonus-report included; its dropdown's /bonus-report/cycles has the same guard)
 *   staff-list                : RequirePermission("export:staff-list", ["admin","hr_admin"])
 *   raw F&O entity dumps      : RequireRole(RAW_FO_ROLES), no key — never grantable
 *
 * Getting these wrong is user-visible in both directions: too narrow and someone
 * who CAN run an export never sees it; too wide and they get a button that 403s.
 */

/** Roles that imply EVERY export — just the platform admins. Anyone else sees
 *  exactly the exports they were granted, and nothing more. */
export const EXPORT_ROLES = ['admin', 'hr_admin'] as const

/** Staff List — a full Sage take-on extract (salaries, contacts, hierarchy). */
export const STAFF_LIST_ROLES = ['admin', 'hr_admin'] as const

/** Roles that may see the raw F&O entity grid (bank accounts, ID numbers).
 *  Deliberately NOT grantable — matches fo_export._RAW_FO_ROLES. */
export const RAW_FO_ROLES = ['admin', 'hr_admin'] as const

export const EXPORT_KEYS = {
  staffList: 'export:staff-list',
  professionalDetails: 'export:professional-details',
  lineManager: 'export:line-manager',
  onboardingObjectives: 'export:onboarding-objectives',
  checkinCalibration: 'export:checkin-calibration',
  positionGrade: 'export:position-grade',
  headcount: 'export:headcount',
  bonusReport: 'export:bonus-report',
} as const

export const EXPORT_PERMISSION_KEYS = Object.values(EXPORT_KEYS)

/** Key → the roles that already imply it, mirroring each endpoint's guard. */
export const EXPORT_KEY_ROLES: Record<string, readonly string[]> = {
  [EXPORT_KEYS.staffList]: STAFF_LIST_ROLES,
  [EXPORT_KEYS.professionalDetails]: EXPORT_ROLES,
  [EXPORT_KEYS.lineManager]: EXPORT_ROLES,
  [EXPORT_KEYS.onboardingObjectives]: EXPORT_ROLES,
  [EXPORT_KEYS.checkinCalibration]: EXPORT_ROLES,
  [EXPORT_KEYS.positionGrade]: EXPORT_ROLES,
  [EXPORT_KEYS.headcount]: EXPORT_ROLES,
  [EXPORT_KEYS.bonusReport]: EXPORT_ROLES,
}
